import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Camera,
  Gift,
  Sparkles,
  Zap,
  Star,
  Stethoscope,
  Heart,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LS_KEY = "dyntra_profile_v1";

const emptyProfile = {
  full_name: "",
  dob: "",
  gender: "Female",
  phone: "",
  alt_phone: "",
  email: "",
  blood_group: "",
  emergency_contact: "",
  profession: "Pharmacist",
  workplace: "",
  experience: "5+ Years",
  preferred_language: "English",
  units: "Metric (cm, kg)",
  temperature: "Celsius (°C)",
  newsletter: "Subscribed",
};

function readProfile() {
  try {
    return { ...emptyProfile, ...JSON.parse(localStorage.getItem(LS_KEY) || "{}") };
  } catch {
    return { ...emptyProfile };
  }
}

export default function Profile() {
  const { user } = useAuth();
  const [form, setForm] = useState(() => {
    const stored = readProfile();
    return {
      ...stored,
      full_name: stored.full_name || user?.name || "",
      email: stored.email || user?.email || "",
    };
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const completion = useMemo(() => {
    const fields = [
      "full_name",
      "dob",
      "gender",
      "phone",
      "email",
      "profession",
      "workplace",
    ];
    const filled = fields.filter((k) => (form[k] || "").toString().trim().length > 0)
      .length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  const save = () => {
    localStorage.setItem(LS_KEY, JSON.stringify(form));
    toast.success("Profile updated");
  };

  const initials = (form.full_name || user?.name || "You")
    .split(" ")
    .map((w) => w.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div data-testid="account-profile" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          My Profile
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your personal information and account settings
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-gray-100 bg-white p-5 sm:p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="relative">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white text-2xl font-bold flex items-center justify-center">
                {initials}
              </div>
              <button
                type="button"
                aria-label="Change profile photo"
                className="absolute bottom-0 right-0 h-7 w-7 rounded-full bg-[#4B2AAD] text-white flex items-center justify-center border-4 border-white"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="min-w-0">
              <h2 className="font-display text-xl font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                {form.full_name || "Welcome!"}
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F3EFFC] px-2 py-0.5 text-[10px] font-bold text-[#4B2AAD] uppercase tracking-wide">
                  <Sparkles className="h-3 w-3" /> Premium Member
                </span>
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">📞 {form.phone || "Add phone"}</p>
              <p className="text-xs text-gray-500">✉️ {form.email || "Add email"}</p>
              <p className="text-[11px] text-gray-400 mt-1">
                Member ID: DYNTRA-{(user?.id || "245678").toString().slice(-6)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-sm font-bold text-gray-900">
              Profile Completion
            </h3>
            <span className="text-xl font-bold text-[#4B2AAD]">{completion}%</span>
          </div>
          <div className="h-2 rounded-full bg-[#F3EFFC] mt-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#4B2AAD] to-[#6B4CD9] rounded-full transition-all"
              style={{ width: `${completion}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-3 flex items-start gap-2">
            <Gift className="h-4 w-4 text-[#4B2AAD] shrink-0 mt-0.5" />
            Almost there! Complete your profile to unlock personalized benefits.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-gray-100 bg-white p-5 sm:p-6">
          <h3 className="font-display text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Stethoscope className="h-4 w-4 text-[#4B2AAD]" />
            Personal Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Full Name" value={form.full_name} onChange={(v) => set("full_name", v)} testid="pf-name" />
            <Field label="Date of Birth" type="date" value={form.dob} onChange={(v) => set("dob", v)} testid="pf-dob" />
            <Select
              label="Gender"
              value={form.gender}
              onChange={(v) => set("gender", v)}
              options={["Female", "Male", "Non-binary", "Prefer not to say"]}
              testid="pf-gender"
            />
            <Field label="Phone Number" value={form.phone} onChange={(v) => set("phone", v)} testid="pf-phone" />
            <Field label="Email Address" type="email" value={form.email} onChange={(v) => set("email", v)} testid="pf-email" />
            <Field label="Alternate Phone (Optional)" value={form.alt_phone} onChange={(v) => set("alt_phone", v)} testid="pf-alt" />
            <Field label="Blood Group (Optional)" value={form.blood_group} onChange={(v) => set("blood_group", v)} testid="pf-blood" />
            <Field label="Emergency Contact (Optional)" value={form.emergency_contact} onChange={(v) => set("emergency_contact", v)} testid="pf-emc" full />
          </div>
          <button
            type="button"
            onClick={save}
            data-testid="pf-save"
            className="dyntra-btn-primary mt-5"
          >
            Save Changes
          </button>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-100 bg-white p-5">
            <h3 className="font-display text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Star className="h-4 w-4 text-[#4B2AAD]" />
              Professional Details
            </h3>
            <dl className="space-y-3 text-sm">
              <Row label="Profession" value={form.profession} />
              <Row label="Workplace" value={form.workplace || "Add workplace"} />
              <Row label="Experience" value={form.experience} />
            </dl>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-5">
            <h3 className="font-display text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Heart className="h-4 w-4 text-[#4B2AAD]" />
              Saved Preferences
            </h3>
            <dl className="space-y-3 text-sm">
              <Row label="Preferred Language" value={form.preferred_language} />
              <Row label="Units" value={form.units} />
              <Row label="Temperature" value={form.temperature} />
              <Row label="Newsletter" value={form.newsletter} />
            </dl>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-display text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#4B2AAD]" />
            Account Security
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Keep your account secure with these settings.
          </p>
          <SecurityRow label="Password" value="••••••••" action="Change" />
          <SecurityRow label="Two-Step Verification" value="Enabled" action="Manage" />
          <SecurityRow label="Login Sessions" value="3 active sessions" action="View" last />
        </div>
        <div className="rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-5">
          <h3 className="font-display text-base font-bold text-gray-900">
            Complete your profile to enjoy personalized health experiences
          </h3>
          <ul className="mt-4 space-y-3 text-sm text-gray-700">
            <li className="flex items-center gap-2"><Zap className="h-4 w-4 text-[#4B2AAD]" /> Better product recommendations</li>
            <li className="flex items-center gap-2"><Zap className="h-4 w-4 text-[#4B2AAD]" /> Faster checkout experience</li>
            <li className="flex items-center gap-2"><Zap className="h-4 w-4 text-[#4B2AAD]" /> Exclusive member benefits</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", full, testid }) {
  return (
    <div className={full ? "sm:col-span-3" : ""}>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm"
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2 outline-none text-sm bg-white"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function SecurityRow({ label, value, action, last }) {
  return (
    <div
      className={`flex items-center justify-between py-3 ${last ? "" : "border-b border-gray-100"}`}
    >
      <div>
        <p className="text-sm font-semibold text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{value}</p>
      </div>
      <button className="text-sm font-semibold text-[#4B2AAD] hover:underline">
        {action}
      </button>
    </div>
  );
}
